import React, { forwardRef, memo, useMemo } from 'react';
import { Link } from '../Link';
import { Loader } from '../Loader';
import { DARK_VIEWS } from './consts';
import { getClass, getClasses } from '@src/utils/getClass';
import { classnames } from '@src/utils/classnames';
import { TColor } from '@src/models/common';
import { IButtonProps } from './models';
import { useExperiment } from '@src/hooks/useExperiment';
import { InputIcon } from '../InputIcon';
import { onUnitClickCallback } from '@src/core/Unit';
import { TooltipNew as Tooltip } from '@src/atoms/TooltipNew';

import styled from './Button.scss';
import { Box } from '../Box';

/** Стандартная кнопка */
export const Button = memo(
  forwardRef<HTMLButtonElement, IButtonProps>((props, ref) => {
    const {
      children,
      fullWidth,
      className,
      view = 'primaryLight',
      size = 'sm',
      postIcon,
      preIcon,
      loading,
      type = 'button',
      disabled,
      id,
      href,
      target,
      iconSize: iconSizeProps,
      disableNextJs,
      onClick,
      tooltipContent,
      tooltipPlacement,
      ...rest
    } = useExperiment(props);

    /** Определяем отдельно класс для текстовых кнопок т.к. у них размеры другие */
    const textClass =
      view === 'textDark' || view === 'textLight' ? getClass(styled, `size_${size}`, 'text') : undefined;

    const classes = classnames(
      styled.root,
      className,
      textClass,
      ...getClasses(styled, { view, size, iconOnly: !children ? size : '' }),
      {
        [styled.iconPre]: !!children && !!preIcon,
        [styled.iconPost]: !!children && !!postIcon,
        [styled.fullWidth]: !!children && fullWidth,
      },
    );

    const linkClasses = classnames({
      [styled.fullWidth]: fullWidth,
    });

    const iconView: TColor = useMemo(() => {
      return DARK_VIEWS.some((v) => v === view) || disabled ? 'basicWhite' : 'basicBlack';
    }, [disabled, view]);

    const iconSize = iconSizeProps || (size === 'lg' ? 'md-light' : 'sm');

    return (
      <Link.Wrapper id={id} href={href} disableNextJs={disableNextJs} target={target} className={linkClasses}>
        <Tooltip content={disabled ? tooltipContent : ''} trigger="hover" placement={tooltipPlacement} maxWidth={128}>
          <Box fullWidth={fullWidth}>
            <button
              data-id={id}
              ref={ref}
              type={type}
              className={classes}
              aria-disabled={disabled}
              disabled={disabled}
              onClick={onUnitClickCallback(id, onClick)}
              {...rest}
            >
              {loading ? (
                <Loader view={iconView === 'basicBlack' ? 'dark' : 'white'} />
              ) : (
                <>
                  {preIcon && <InputIcon size={iconSize} config={preIcon} view={iconView} disabled={disabled} />}
                  {children}
                  {postIcon && <InputIcon size={iconSize} config={postIcon} view={iconView} disabled={disabled} />}
                </>
              )}
            </button>
          </Box>
        </Tooltip>
      </Link.Wrapper>
    );
  }),
);

Button.displayName = 'Button';
